import { fetchShopifyCartUrl, getShopifyCartById } from '@portal/api/shopifyV2/cart';
import { updateShopifyCartLineProperties } from '@portal/api/shopifyV2/cart/cartLinesPropertiesUpdate';
import { updateShopifyCartAttributes } from '@portal/api/shopifyV2/cart/cartAttributesUpdate';
import { LocaleStore, ShopifyLocaleCode } from '@portal/schema/locale';
import { useStatsigClient } from '@statsig/react-bindings';
import cookies from 'js-cookie';
import { useCallback } from 'react';
import { StoreCode } from 'schema/storeCode';
import { useRudderAnalytics } from '../providers/RudderAnalytics';

/**
 * Hook for generating Shopify cart URLs with analytics tracking parameters
 */
export const useShopifyCartUrl = () => {
  const rudderAnalytics = useRudderAnalytics();
  const { client } = useStatsigClient();
  const {
    stableID,
    user: { userID }
  } = client.getContext();

  /**
   * Generate a Shopify cart URL with analytics tracking parameters
   */
  const getShopifyCartUrl = useCallback(
    async (
      storeCode: StoreCode,
      cartId: string,
      locale: keyof typeof ShopifyLocaleCode
    ): Promise<string> => {
      const baseUrl = await fetchShopifyCartUrl(storeCode, cartId, locale);

      // Fetch update properties with relevant metafields
      await updateShopifyCartLineProperties(storeCode, cartId);

      // Update existing cart attributes with analytics properties
      const cart = await getShopifyCartById(storeCode, cartId);

      const attributesInput = {
        cartId: cart.id,
        attributes: [...(cart.attributes || [])]
      };

      const rudderAnonymousId = localStorage.getItem('rudder_anonymous_id');
      if (rudderAnonymousId) {
        attributesInput.attributes.push({
          key: 'rudderAnonymousId',
          value: rudderAnonymousId
        });
      }

      const rudderSessionId = sessionStorage.getItem('rudder_session_id');
      if (rudderSessionId) {
        attributesInput.attributes.push({
          key: 'rudderSessionId',
          value: rudderSessionId
        });
      }

      await updateShopifyCartAttributes(storeCode, attributesInput);

      try {
        const url = new URL(baseUrl, window.location.origin);

        // used to set store flags in shopify GTM
        // example: window.is_us_store = region === 'us');
        url.searchParams.set('region', LocaleStore[locale]);

        // used to set locale flags in shopify GTM
        // example: window.is_apac_locale = locale === 'apac-us');
        url.searchParams.set('ag_locale', locale);

        if (rudderAnalytics) {
          const anonymousId = rudderAnalytics.getAnonymousId();
          if (anonymousId) {
            url.searchParams.set('ajs_aid', anonymousId);
          }
        }

        const slTrackCode = cookies.get('sl-track-purchase');
        const slShopCode = cookies.get('sl-shop-code');

        if (slTrackCode && slShopCode) {
          url.searchParams.set('SLTrackCode', slTrackCode);
          url.searchParams.set('SLShopCode', slShopCode);
        }

        // Shopify's custom pixel consumes statsig URL parameters.
        // https://docs.statsig.com/client/javascript-sdk/stable-id/#persisting-stableid-across-subdomain
        if (stableID) {
          url.searchParams.set('ss_stable_id', stableID);
        }
        if (userID) {
          url.searchParams.set('ss_user_id', userID);
        }

        return url.toString();
      } catch (_e) {
        return baseUrl;
      }
    },
    [rudderAnalytics, stableID, userID]
  );

  return { getShopifyCartUrl };
};
